/**
 * Image Helper Utilities
 * Handles image URLs for both development and production environments
 */

// Get the base URL for the backend (without /api)
export const getBackendBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return apiUrl.replace('/api', '');
};

/**
 * Get full URL for storage files
 * @param {string} path - The path from storage (e.g., 'uploads/image.jpg' or full URL)
 * @returns {string} Full URL (e.g., 'http://localhost:8000/storage/uploads/image.jpg')
 * 
 * Note: The API now returns full URLs from model accessors, so this function
 * mainly handles legacy paths or when the API hasn't been updated yet.
 */
export const getStorageUrl = (path) => {
  if (!path) return null;
  
  // If it's already a full URL (from API accessors), return it directly
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const baseUrl = getBackendBaseUrl();

  // Normalise common Laravel storage paths:
  // - 'public/xyz.jpg'        -> '/xyz.jpg'
  // - 'storage/xyz.jpg'       -> '/xyz.jpg'
  // - '/storage/xyz.jpg'      -> '/xyz.jpg'
  let normalisedPath = path.trim();

  // Strip leading slashes for easier checks
  normalisedPath = normalisedPath.replace(/^\/+/, '');

  if (normalisedPath.startsWith('public/')) {
    normalisedPath = normalisedPath.substring('public/'.length);
  } else if (normalisedPath.startsWith('storage/')) {
    normalisedPath = normalisedPath.substring('storage/'.length);
  }

  const cleanPath = normalisedPath.startsWith('/') ? normalisedPath : `/${normalisedPath}`;

  return `${baseUrl}/storage${cleanPath}`;
};

/**
 * Get full URL for any backend resource
 * @param {string} path - The path (e.g., '/storage/image.jpg')
 * @returns {string} Full URL
 */
export const getBackendUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path; // Already full URL
  const baseUrl = getBackendBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Create image element with error handling
 * @param {string} src - Image source
 * @param {string} alt - Alt text
 * @param {string} fallback - Fallback image path
 * @returns {object} Props for img element
 */
export const getImageProps = (src, alt = 'Image', fallback = '/placeholder.png') => {
  return {
    src: src || fallback,
    alt,
    onError: (e) => {
      if (e.target.src !== fallback) {
        e.target.src = fallback;
      }
    }
  };
};

export default {
  getStorageUrl,
  getBackendUrl,
  getBackendBaseUrl,
  getImageProps
};

